import { Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useInsuranceApi } from '../../../../../hooks/use-insurance-api';
import { UserError } from '../../../../../services/insurance/types/user-error';
import { CloseButton, DialogSubTitle, DialogToolbar } from '../../../../shared/dialog/dialog.styles';
import { withPatientIvStatusDialogProvider } from '../../hooks/use-patient-iv-status-dialog/hoc';
import { usePatientIvStatusDialog } from '../../hooks/use-patient-iv-status-dialog/hook';
import { PatientIvStatusDialog } from '../patient-iv-status-dialog';
import { AppointmentEndedMessage } from '../shared/appointment-ended-message/appointment-ended-message';
import { ManuallyVerifiedButton } from '../shared/manually-verified-button';
import { RunVerificationButton } from '../shared/run-verification-button';
import { SelfPayButton } from '../shared/self-pay-button';
import { BoldText, InsuranceErrorList, InsuranceErrorListItem } from '../styles';

const RequestDataInvalidStatusDialog: FC = () => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { status, getVerificationResponse } = useInsuranceApi();
  const { opened, onClose, isLoading, locationId, patientInfo, selectedIvSummary, appointment } =
    usePatientIvStatusDialog();

  useEffect(() => {
    if (!opened || !selectedIvSummary) return;

    (async () => {
      const verificationResponse = await getVerificationResponse(locationId, selectedIvSummary.idVerificationRequest);

      if (verificationResponse && verificationResponse.rawResponse) {
        const userErrors: UserError[] = JSON.parse(verificationResponse.rawResponse);

        setValidationErrors(userErrors.map((e) => e.message));
      }
    })();
  }, [opened, selectedIvSummary]);

  return (
    <PatientIvStatusDialog
      opened={opened}
      title="Verification Request Data Invalid"
      subtitle={<BoldText>{`${patientInfo?.firstName} ${patientInfo?.lastName}`}</BoldText>}
    >
      <DialogSubTitle
        sx={{ margin: '10px 0 20px 0', display: 'block' }}
        isLoading={isLoading || status.getVerificationResponse.isRunning}
      >
        {validationErrors.length > 0 && (
          <>
            The insurance verification process encountered the following issues:
            <InsuranceErrorList>
              {validationErrors.map((error, index) => (
                <InsuranceErrorListItem key={index}>{error}</InsuranceErrorListItem>
              ))}
            </InsuranceErrorList>
            <Typography sx={{ marginBottom: '20px' }}>
              Once these issue are corrected you can try rerunning the insurance verification.
            </Typography>
          </>
        )}
        {validationErrors.length === 0 && (
          <>All invalid data issues have been corrected. Please try rerunning the verification again.</>
        )}
      </DialogSubTitle>
      <AppointmentEndedMessage />
      <DialogToolbar>
        <ManuallyVerifiedButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <SelfPayButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <RunVerificationButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <CloseButton onClick={onClose} disabled={isLoading} />
      </DialogToolbar>
    </PatientIvStatusDialog>
  );
};

export default withPatientIvStatusDialogProvider(RequestDataInvalidStatusDialog);
